import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2Icon, Maximize2Icon, CropIcon } from 'lucide-react';
import { BackgroundRemovalButton } from './BackgroundRemovalButton';
interface Design {
  id: string;
  image: string;
  x: number;
  y: number;
  scale: number;
  zIndex: number;
}
interface DesignControlsProps {
  designs: Design[];
  selectedDesignId: string | null;
  selectedDesign: Design | undefined;
  onDesignSelect: (id: string) => void;
  onScaleChange: (scale: number) => void;
  onRemoveDesign: (id: string) => void;
  onRemoveBackground: (id: string) => void;
  onUpscaleImage: (id: string) => void;
  onAutoTrim: (id: string) => void;
}
export function DesignControls({
  designs,
  selectedDesignId,
  selectedDesign,
  onDesignSelect,
  onScaleChange,
  onRemoveDesign,
  onRemoveBackground,
  onUpscaleImage,
  onAutoTrim
}: DesignControlsProps) {
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    if (selectedDesign) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.width,
          height: img.height
        });
      };
      img.src = selectedDesign.image;
    } else {
      setImageDimensions(null);
    }
  }, [selectedDesign]);
  if (designs.length === 0) return null;
  const printWidth = imageDimensions ? (imageDimensions.width / 300).toFixed(2) : '0';
  const printHeight = imageDimensions ? (imageDimensions.height / 300).toFixed(2) : '0';
  return <div className="space-y-4" data-id="element-51">
      <div data-id="element-52">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-53">
          Design Layers
        </h3>
        <div className="space-y-2" data-id="element-54">
          {designs.map((design, index) => <div key={design.id} onClick={() => onDesignSelect(design.id)} className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedDesignId === design.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-55">
              <div className="flex items-center justify-between" data-id="element-56">
                <div className="flex items-center gap-3" data-id="element-57">
                  <img src={design.image} alt={`Design ${index + 1}`} className="w-12 h-12 object-contain rounded bg-gray-50" data-id="element-58" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-59">
                    Layer {index + 1}
                  </span>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              onRemoveDesign(design.id);
            }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-60">
                  <Trash2Icon className="w-4 h-4" data-id="element-61" />
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {selectedDesign && <div className="pt-4 border-t border-gray-200" data-id="element-62">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-63">
            Layer Controls
          </h3>

          {imageDimensions && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-64">
              <h4 className="text-xs font-semibold text-blue-900 mb-2" data-id="element-65">
                📏 Art Specifications
              </h4>
              <div className="space-y-1 text-xs text-blue-800" data-id="element-66">
                <div className="flex justify-between" data-id="element-67">
                  <span className="font-medium" data-id="element-68">Resolution:</span>
                  <span className="font-mono" data-id="element-69">
                    {imageDimensions.width} × {imageDimensions.height} px
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-70">
                  <span className="font-medium" data-id="element-71">Print Size @ 300 DPI:</span>
                  <span className="font-mono" data-id="element-72">
                    {printWidth}" × {printHeight}"
                  </span>
                </div>
              </div>
            </div>}

          <div className="space-y-3" data-id="element-73">
            <div data-id="element-74">
              <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-75">
                Size: {Math.round(selectedDesign.scale * 100)}%
              </label>
              <input type="range" min="0.1" max="2" step="0.05" value={selectedDesign.scale} onChange={e => onScaleChange(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-76" />
            </div>

            <div className="space-y-2" data-id="element-77">
              <button onClick={() => onRemoveBackground(selectedDesign.id)} className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-78">
                Remove Background
              </button>

              <button onClick={() => onUpscaleImage(selectedDesign.id)} className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-79">
                <Maximize2Icon className="w-4 h-4" data-id="element-80" />
                Upscale Image (4K)
              </button>

              <button onClick={() => onAutoTrim(selectedDesign.id)} className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-81">
                <CropIcon className="w-4 h-4" data-id="element-82" />
                Auto Trim Dead Space
              </button>
            </div>
          </div>
        </div>}
    </div>;
}